/**
 * NavLinksClient Component
 *
 * Diamond Architecture: Client Component version of NavLinks.
 * Used in NavbarShell (Client Component).
 * Core navigation links (Rent / Buy) + optional section link.
 *
 * The ACTIVE item is not decided here. `activeSection` is
 * `activeNavSection(routeId, browseScope)` from the generated
 * `nav-section_gen.ts` (webroutesgen, over the routes manifest's `browse: true`
 * rows), computed by `SiteNavbar` — so the rent hub, the rent type facets and a
 * district's rent facet are all "Rent" without this file knowing a path.
 */
import { Link } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { NavSection } from '@/shared/lib/nav-section_gen';
import { routes, type Route } from '@letsgethome/routes';
import type { LocalizedText } from '@letsgethome/types/branded';

export interface SectionLink {
  /** Visible link text — must be `t()`-translated, never a raw literal. */
  label: LocalizedText;
  href: Route;
}

interface NavLinksClientProps {
  /** Optional section link (e.g., Blog, Estates) - shown as active */
  sectionLink?: SectionLink;
  /** The primary section the current page is in, or `null` for none. */
  activeSection?: NavSection | null;
}

const linkStyles =
  'rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';
const activeLinkStyles = 'rounded-md px-3 py-2 text-sm font-medium text-foreground bg-muted';

export function NavLinksClient({ sectionLink, activeSection = null }: NavLinksClientProps) {
  const t = useTranslations('nav');
  const tA11y = useTranslations('common');

  // One row per section, exhaustively: a new `NavSection` fails this
  // `Record` until it has a link.
  const sections: Record<NavSection, { href: Route; label: LocalizedText }> = {
    rent: { href: routes.listings.rent(), label: t('rent') },
    buy: { href: routes.listings.buy(), label: t('buy') },
  };
  const order: readonly NavSection[] = ['rent', 'buy'];

  return (
    <nav aria-label={tA11y('a11y.primaryNavigation')}>
      <ul className="flex gap-1">
        {order.map((section) => {
          const active = section === activeSection;
          return (
            <li key={section}>
              <Link
                href={sections[section].href}
                prefetch="intent"
                aria-current={active ? 'page' : undefined}
                className={active ? activeLinkStyles : linkStyles}
              >
                {sections[section].label}
              </Link>
            </li>
          );
        })}
        {sectionLink && (
          <li>
            <Link
              href={sectionLink.href}
              prefetch="intent"
              aria-current="page"
              className={activeLinkStyles}
            >
              {sectionLink.label}
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
}
